import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { filterUtils } from '@renderer/utils/filterUtils'
import { applyLUTThenOverlay } from '@renderer/utils/lutUtils'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { COUNTDOWN_PLACEHOLDER, useCountdown } from '@renderer/utils/countdownUtils'
import Preview from '@renderer/components/Preview'
import Button from '@renderer/components/Button'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { FaChevronLeft } from 'react-icons/fa'
import { FaChevronRight } from 'react-icons/fa'
import { imageUtils } from '@renderer/utils'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useTranslation } from 'react-i18next'
import { useSharedCountdown } from './hooks/useSharedCountdown'
import { backgroundFilterProcessor } from '@renderer/utils/backgroundFilterProcessor'
import ProcessingModal from '@renderer/components/ProcessingModal'
import { useDesignLayout } from '@renderer/hooks'

/** Match `Preview.tsx` / `imageUtils.applyRotationMirroring` so strip pixels align with the main live feed. */
async function bakeStripThumbnailOrientation(
  canvas: HTMLCanvasElement,
  rotation: number,
  mirror: boolean
): Promise<void> {
  const r = ((rotation % 360) + 360) % 360
  if (r === 0 && !mirror) return
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/png')
  })
  if (!blob) return
  const oriented = await imageUtils.applyRotationMirroring(blob, r, mirror)
  const bitmap = await createImageBitmap(oriented)
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) {
    bitmap.close()
    return
  }
  canvas.width = bitmap.width
  canvas.height = bitmap.height
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(bitmap, 0, 0)
  bitmap.close()
}

/** Draws filter-strip thumbnail from an offscreen canvas; useLayoutEffect avoids ref-callback redraw storms. */
const FilterCarouselThumb: React.FC<{ source: HTMLCanvasElement | undefined }> = ({ source }) => {
  const ref = useRef<HTMLCanvasElement>(null)
  useLayoutEffect(() => {
    const canvas = ref.current
    if (!canvas || !source) return
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.clearRect(0, 0, 144, 144)
    ctx.drawImage(source, 0, 0, 144, 144)
  }, [source])
  return <canvas width={144} height={144} className="w-full h-full object-cover" ref={ref} />
}

// Component to display filtered captured image
// Optimized to use cached filtered versions when available to avoid expensive recalculation
const FilteredImagePreview: React.FC<{
  imageUrl: string
  filterAdjustments?: Record<string, number>
  cachedFilteredUrl?: string
  filterMode?: 'manual' | 'lut'
  filterId?: string
  postLUTAdjustments?: { vignette?: number; grain?: number; blur?: number; clarity?: number; sharpeningAmount?: number }
}> = ({ imageUrl, filterAdjustments, cachedFilteredUrl, filterMode, filterId, postLUTAdjustments }) => {
  const [filteredImageUrl, setFilteredImageUrl] = useState<string>(cachedFilteredUrl || imageUrl)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (cachedFilteredUrl) {
      setFilteredImageUrl(cachedFilteredUrl)
      return
    }

    const canvas = canvasRef.current
    if (!canvas) return

    let cancelled = false

    void (async (): Promise<void> => {
      try {
        // Match bottom carousel + applyFilterToImage: createImageBitmap applies EXIF orientation.
        // drawImage(HTMLImageElement) often ignores orientation, so main preview looked rotated vs thumbs.
        const response = await fetch(imageUrl)
        if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`)
        const blob = await response.blob()
        const bitmap = await createImageBitmap(blob)
        if (cancelled) {
          bitmap.close()
          return
        }

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          bitmap.close()
          return
        }

        canvas.width = bitmap.width
        canvas.height = bitmap.height
        ctx.drawImage(bitmap, 0, 0)

        if (filterMode === 'lut' && filterId) {
          applyLUTThenOverlay(ctx, {
            _id: filterId,
            filterMode: 'lut',
            postLUTAdjustments: postLUTAdjustments ?? undefined
          })
        } else if (filterAdjustments) {
          filterUtils.applyAdjustments(ctx, filterAdjustments)
        }

        bitmap.close()

        if (!cancelled) {
          setFilteredImageUrl(canvas.toDataURL())
        }
      } catch (e) {
        console.error('Error loading image for filter preview', e)
        if (!cancelled) {
          setFilteredImageUrl(imageUrl)
        }
      }
    })()

    return (): void => {
      cancelled = true
    }
  }, [imageUrl, filterAdjustments, cachedFilteredUrl, filterMode, filterId, postLUTAdjustments])

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <canvas ref={canvasRef} className="hidden" />
      <img
        src={filteredImageUrl}
        alt="Filtered photo"
        className="w-full h-full object-contain"
        crossOrigin="anonymous"
      />
    </div>
  )
}

const FilterPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { filters, config: globalConfig, getPageSettings } = useConfigStore()
  const config = globalConfig?.pages.filter
  const { setSessionState, mode, userPreferences, logActivity, gallery } = useSessionStateStore()
  const sessionStateStore = useSessionStateStore
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null)
  const [previewCanvases, setPreviewCanvases] = useState<{ [key: string]: HTMLCanvasElement }>({})
  const [stripThumbsFromLiveCameraBlob, setStripThumbsFromLiveCameraBlob] = useState(false)
  const [capturedImageUrl, setCapturedImageUrl] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const sliderRef = useRef<Slider>(null)
  const {
    designW: screenWidth,
    designH: screenHeight,
    isLandscape,
    isPortrait: filterPortrait,
    isCompact: filterUiCompact,
    isTiny,
    shortSide
  } = useDesignLayout()
  const {
    captureRatio: primaryCaptureRatio,
    previewRotation: primaryPreviewRotation,
    mirror: primaryMirror
  } = useSettingsStore((state) => state.cameraSettings)
  const {
    captureRatio: secondaryCaptureRatio,
    previewRotation: secondaryPreviewRotation,
    mirror: secondaryMirror
  } = useSettingsStore((state) => state.secondaryCameraSettings)

  /**
   * Must match `Preview.tsx`: `settings = cameraKey === 'primary' ? cameraSettings : secondaryCameraSettings`.
   * Only the literal `'primary'` uses primary; `undefined`, `'secondary'`, or anything else uses secondary.
   * Filter previously used `=== 'secondary' ? secondary : primary`, which picked primary when undefined —
   * so strip rotation/crop stayed at 0° while the main preview followed secondary camera settings.
   */
  const previewCameraKey = useMemo(
    (): 'primary' | 'secondary' =>
      userPreferences.selectedCamera === 'primary' ? 'primary' : 'secondary',
    [userPreferences.selectedCamera]
  )

  const [remainingTime, setRemainingTime] = useState<string>(COUNTDOWN_PLACEHOLDER)
  const welcomeSettings = getPageSettings('welcome')
  
  // Use shared countdown for consistent UI
  const {
    remainingTime: sharedRemainingTime
  } = useSharedCountdown()

  /**
   * Non-compact landscape with modest carousel width (`shortSide`, e.g. 1080 on 1920×1080):
   * default spacious sizing (5× w-36 + padding) overflows — scale thumbs, labels, and slide count.
   */
  const filterStripLandscapeTight =
    isLandscape && !filterUiCompact && !isTiny && shortSide <= 1200

  const slidesToShow = Math.max(
    1,
    Math.min(
      isTiny ? 3 : filterUiCompact ? 3 : filterStripLandscapeTight ? 4 : 5,
      Math.max(1, filters.length)
    )
  )
  const filterThumbClass = isTiny
    ? 'w-16 h-16'
    : filterUiCompact
      ? 'w-24 h-24'
      : filterStripLandscapeTight
        ? 'w-28 h-28'
        : 'w-36 h-36'
  const filterSliderRowClass = isTiny
    ? 'h-[100px]'
    : filterUiCompact
      ? 'h-[140px]'
      : filterStripLandscapeTight
        ? 'h-[160px]'
        : 'h-[220px]'
  const filterSlidePadding = filterUiCompact
    ? 'px-2 py-2'
    : filterStripLandscapeTight
      ? 'px-3 py-3'
      : 'px-6 py-6'
  const filterNameTextClass = isTiny
    ? 'text-sm'
    : filterUiCompact
      ? 'text-lg'
      : filterStripLandscapeTight
        ? 'text-base'
        : 'text-2xl'
  const filterSelectedScale = filterStripLandscapeTight ? 1.08 : 1.15
  // Portrait: reserve space for Next (bottom-10 / bottom-24 + button + optional countdown) + label row; keep slider above chrome
  const filterSliderBottomPx = filterUiCompact
    ? isLandscape
      ? 6
      : filterPortrait
        ? isTiny
          ? 104
          : 124
        : isTiny
          ? 40
          : 56
    : isLandscape
      ? 12
      : filterPortrait
        ? 228
        : 144

  // Stable value for effect deps — inline helpers change identity every render and would retrigger fetch
  /** Same L2 crop as main `<Preview cameraKey={...} />` — must track selected camera. */
  const normalizedCaptureRatio = useMemo((): number | undefined => {
    const raw =
      previewCameraKey === 'secondary' ? secondaryCaptureRatio : primaryCaptureRatio
    if (raw === 'auto') return undefined
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
      return raw
    }
    return undefined
  }, [primaryCaptureRatio, secondaryCaptureRatio, previewCameraKey])

  const applyAfterCapture = config?.settings?.applyAfterCapture || false

  /**
   * Live filter strip: match `<Preview />` orientation — CSS rotate + rotation-aware mirror only.
   * Pixel-baking via `applyRotationMirroring` after `processPreview` can diverge from Preview's
   * `translate → rotate → mirror` composition, so thumbs looked rotated differently from the main feed.
   *
   * Only skip this when the main area shows a captured still (`FilteredImagePreview`): that bitmap is
   * already display-oriented. If `applyAfterCapture` is on but there is no `capturedImageUrl` yet, the
   * main area still renders `<Preview />` while strip thumbs come from `processPreview` (sensor-oriented
   * pixels) — same as pre-capture mode — so we must keep CSS rotation/mirror or thumbs stay at 0°.
   *
   * When strip thumbnails are built from `capturePreview` blobs, we bake rotation/mirror into the canvas
   * (`bakeStripThumbnailOrientation`) so they match Preview even if nested CSS transforms misbehave.
   */
  const liveFilterStripTransform = useMemo((): string | undefined => {
    if (applyAfterCapture && capturedImageUrl) return undefined
    if (stripThumbsFromLiveCameraBlob) return undefined
    const rotation =
      previewCameraKey === 'secondary' ? secondaryPreviewRotation : primaryPreviewRotation
    const mirror =
      (previewCameraKey === 'secondary' ? secondaryMirror : primaryMirror) ?? true
    const r = rotation ?? 0
    const mirrorTransform = imageUtils.getRotationAwareMirrorTransform(r, mirror)
    const rotateTransform = r ? `rotate(${r}deg)` : ''
    const combined = [rotateTransform, mirrorTransform].filter(Boolean).join(' ').trim()
    return combined.length > 0 ? combined : undefined
  }, [
    applyAfterCapture,
    capturedImageUrl,
    stripThumbsFromLiveCameraBlob,
    previewCameraKey,
    primaryPreviewRotation,
    secondaryPreviewRotation,
    primaryMirror,
    secondaryMirror
  ])

  const filterPageUseLivePreview = config?.settings?.useLivePreview ?? true
  const filterPreviewImageUrlConfig = globalConfig?.filterPreviewImageUrl
  // Only depend on gallery when post-capture filter thumbs use the captured photo
  const filterPreviewGallerySourceKey = applyAfterCapture
    ? (gallery.capturedItems[0]?.photoUrl ?? '')
    : ''
  const nextPage = applyAfterCapture
    ? 'select'
    : mode === 'capture'
      ? 'frame'
      : welcomeSettings?.startWithPayment
        ? 'record'
        : 'checkout'

  // Determine capture mode
  const captureMode = globalConfig?.pages.capture?.settings?.mode || 'standard'

  // Determine if Filter page is before or after capture
  // If applyAfterCapture is false, Filter appears BEFORE capture (no global countdown yet)
  // If applyAfterCapture is true, Filter appears AFTER capture (use appropriate countdown)
  const isAfterCapture = applyAfterCapture

  // Initialize countdown with the useCountdown hook
  const filterTimeout = config?.settings?.filterTimeout || 0
  const countdown = useCountdown(filterTimeout)

  useEffect(() => {
    if (filters.length > 0) {
      setSelectedFilter(filters[0]._id)
    }
  }, [filters])

  const handleNext = async (): Promise<void> => {
    if (!selectedFilter || isProcessing) return

    setIsProcessing(true)

    try {
      setSessionState({
        userPreferences: {
          filterId: selectedFilter
        }
      })
      const filterName = filters.find((f) => f._id === selectedFilter)?.name
      logActivity(`Selected filter: ${filterName || selectedFilter}`, { filterId: selectedFilter })

      // If applyAfterCapture is enabled, process all individual photos with the selected filter
      if (applyAfterCapture) {
        // Get all captured photos (excluding strips, gifs, etc.)
        const photosToProcess = gallery.capturedItems.filter(
          (item) =>
            item.photoUrl &&
            !item.id?.includes('dreambooth-strip') &&
            !item.id?.includes('dreambooth-gif') &&
            !item.id?.includes('dreambooth-animation')
        )

        // Process each photo with the selected filter
        // Use originalPhotoUrl if available to ensure we process the original unfiltered image
        for (const item of photosToProcess) {
          if (item.photoUrl && item.id) {
            // Use originalPhotoUrl if available (to process original), otherwise use photoUrl
            const photoUrlToProcess = item.originalPhotoUrl || item.photoUrl
            await backgroundFilterProcessor.addToQueue(photoUrlToProcess, item.id, selectedFilter)
          }
        }

        // Wait for all filters to be processed
        // Poll until processing is complete (timeout scales with photo count: ~10s per photo)
        const maxWaitTime = Math.max(30000, photosToProcess.length * 10000)
        const startTime = Date.now()
        
        while (Date.now() - startTime < maxWaitTime) {
          const status = backgroundFilterProcessor.getProcessingStatus()
          if (!status.isProcessing && status.queueLength === 0) {
            break
          }
          await new Promise((resolve) => setTimeout(resolve, 100))
        }

        // Verify all photos have filteredVersions (timeout scales with photo count)
        const verifyStartTime = Date.now()
        const maxVerifyTime = Math.max(5000, photosToProcess.length * 2000)
        while (Date.now() - verifyStartTime < maxVerifyTime) {
          const { gallery: verifyGallery } = sessionStateStore.getState()
          const allHaveFilteredVersions = photosToProcess.every((item) => {
            const galleryItem = verifyGallery.capturedItems.find((i) => i.id === item.id)
            return galleryItem?.filteredVersions?.[selectedFilter]
          })
          if (allHaveFilteredVersions) {
            break
          }
          await new Promise((resolve) => setTimeout(resolve, 100))
        }

        // Update gallery items to use filtered versions
        const { gallery: latestGallery } = sessionStateStore.getState()
        const updatedItems = latestGallery.capturedItems.map((item) => {
          if (item.photoUrl && item.filteredVersions?.[selectedFilter]) {
            // Replace photoUrl with filtered version
            return {
              ...item,
              photoUrl: item.filteredVersions[selectedFilter]
            }
          }
          return item
        })

        setSessionState({
          gallery: {
            ...latestGallery,
            capturedItems: updatedItems
          }
        })

        const successCount = updatedItems.filter((item) => item.filteredVersions?.[selectedFilter]).length
        logActivity(`Applied filter ${filterName || selectedFilter} to ${successCount} of ${photosToProcess.length} photos`, {
          filterId: selectedFilter,
          photosProcessed: successCount,
          photosAttempted: photosToProcess.length
        })
      }

      // Navigate based on applyAfterCapture setting
      // When applyAfterCapture=true, always navigate to Select
      const nextPage = applyAfterCapture
        ? 'select'
        : mode === 'capture'
          ? 'frame'
          : welcomeSettings?.startWithPayment
            ? 'record'
            : 'checkout'
      navigate(`/${projectId}/${nextPage}`)
    } catch (error) {
      console.error('Error in handleNext:', error)
      setIsProcessing(false)
    }
  }

  // Page-local filter timeout whenever a per-page countdown applies (not shared frame-based-after-capture)
  const usePageFilterCountdown =
    filterTimeout > 0 && !(isAfterCapture && captureMode === 'frame-based')

  useEffect(() => {
    if (!usePageFilterCountdown) return

    countdown.setOnTick(() => {
      setRemainingTime(countdown.getFormattedRemainingSeconds())
    })

    countdown.setOnComplete(() => {
      const filterName = filters.find((f) => f._id === selectedFilter)?.name
      logActivity(
        `Filter selection timed out. Automatically selected filter: ${filterName || selectedFilter}`,
        {
          filterId: selectedFilter,
          timeoutDuration: filterTimeout
        }
      )
      handleNext()
    })
  }, [selectedFilter, filterTimeout, captureMode, isAfterCapture, usePageFilterCountdown])

  useEffect(() => {
    if (!usePageFilterCountdown) return

    countdown.start()
    return (): void => {
      countdown.stop()
    }
  }, [filterTimeout, captureMode, isAfterCapture, usePageFilterCountdown])

  useEffect(() => {
    if (isAfterCapture && captureMode === 'frame-based') {
      countdown.stop()
    }
  }, [isAfterCapture, captureMode])

  useEffect(() => {
    let cancelled = false

    const createFilterPreviews = async (): Promise<void> => {
      if (cancelled) return
      const previews: { [key: string]: HTMLCanvasElement } = {}
      let thumbsFromLiveCameraBlob = false
      const stripR =
        (previewCameraKey === 'secondary' ? secondaryPreviewRotation : primaryPreviewRotation) ?? 0
      const stripM =
        (previewCameraKey === 'secondary' ? secondaryMirror : primaryMirror) ?? true

      // If applyAfterCapture is enabled, use first captured image
      if (applyAfterCapture && gallery.capturedItems.length > 0) {
        const firstCapturedImageUrl = gallery.capturedItems[0].photoUrl
        if (!firstCapturedImageUrl) {
          if (!cancelled) setStripThumbsFromLiveCameraBlob(false)
          return
        }

        // Store captured image URL for main preview
        setCapturedImageUrl(firstCapturedImageUrl)

        try {
          const response = await fetch(firstCapturedImageUrl)
          const capturedBlob = await response.blob()

          for (const filter of filters) {
            const canvas = document.createElement('canvas')
            canvas.width = 144
            canvas.height = 144
            const ctx = canvas.getContext('2d', {
              willReadFrequently: true
            }) as unknown as OffscreenCanvasRenderingContext2D
            if (ctx) {
              try {
                // Create image from captured blob
                const imageBitmap = await createImageBitmap(capturedBlob)
                const tempCanvas = document.createElement('canvas')
                tempCanvas.width = imageBitmap.width
                tempCanvas.height = imageBitmap.height
                const tempCtx = tempCanvas.getContext('2d', {
                  willReadFrequently: true
                })
                if (tempCtx) {
                  tempCtx.drawImage(imageBitmap, 0, 0)
                  if (filter.filterMode === 'lut') {
                    applyLUTThenOverlay(tempCtx, filter)
                  } else if (filter.adjustments) {
                    filterUtils.applyAdjustments(tempCtx, filter.adjustments)
                  }
                  imageBitmap.close()

                  // Scale and center the filtered image
                  const scale = Math.max(
                    canvas.width / tempCanvas.width,
                    canvas.height / tempCanvas.height
                  )
                  const x = (canvas.width - tempCanvas.width * scale) / 2
                  const y = (canvas.height - tempCanvas.height * scale) / 2
                  ctx.drawImage(
                    tempCanvas,
                    x,
                    y,
                    tempCanvas.width * scale,
                    tempCanvas.height * scale
                  )
                }
              } catch (error) {
                console.error('Error creating preview for filter:', filter._id, error)
              }
            }
            previews[filter._id] = canvas
          }
        } catch (error) {
          console.error('Error loading captured image for filter previews:', error)
          if (!cancelled) setStripThumbsFromLiveCameraBlob(false)
          return
        }
      } else {
        // Original behavior: use cached thumbnails or live camera
        // Clear captured image URL when not in applyAfterCapture mode
        setCapturedImageUrl(null)

        // If useLivePreview is false, use cached thumbnails or generate them from filter preview image
        if (!filterPageUseLivePreview) {
          // Check if we have cached thumbnails
          const hasCachedThumbnails = filters.some((f) => f.cachedThumbnail)

          if (hasCachedThumbnails) {
            // Use existing cached thumbnails
            for (const filter of filters) {
              if (filter.cachedThumbnail) {
                try {
                  const response = await fetch(filter.cachedThumbnail)
                  const blob = await response.blob()
                  const imageBitmap = await createImageBitmap(blob)

                  const canvas = document.createElement('canvas')
                  canvas.width = 144
                  canvas.height = 144
                  const ctx = canvas.getContext('2d', {
                    willReadFrequently: true
                  })

                  if (ctx) {
                    ctx.drawImage(imageBitmap, 0, 0, 144, 144)
                    imageBitmap.close()
                    previews[filter._id] = canvas
                  }
                } catch (error) {
                  console.error('Error loading cached thumbnail for filter:', filter._id, error)
                }
              }
            }
          } else if (
            filterPreviewImageUrlConfig &&
            (filterPreviewImageUrlConfig.startsWith('http') ||
              filterPreviewImageUrlConfig.startsWith('blob:'))
          ) {
            // Generate thumbnails on the fly from filter preview image
            try {
              const response = await fetch(filterPreviewImageUrlConfig)
              const previewBlob = await response.blob()
              const previewImageBitmap = await createImageBitmap(previewBlob)

              for (const filter of filters) {
                const canvas = document.createElement('canvas')
                canvas.width = 144
                canvas.height = 144
                const ctx = canvas.getContext('2d', {
                  willReadFrequently: true
                })

                if (ctx) {
                  // Create a temporary canvas to process the filter
                  const tempCanvas = document.createElement('canvas')
                  tempCanvas.width = previewImageBitmap.width
                  tempCanvas.height = previewImageBitmap.height
                  const tempCtx = tempCanvas.getContext('2d', {
                    willReadFrequently: true
                  })

                  if (tempCtx) {
                    // Draw the preview image
                    tempCtx.drawImage(previewImageBitmap, 0, 0)

                    if (filter.filterMode === 'lut') {
                      applyLUTThenOverlay(tempCtx, filter)
                    } else if (filter.adjustments) {
                      filterUtils.applyAdjustments(tempCtx, filter.adjustments)
                    }

                    // Scale and center the filtered image to thumbnail size
                    const scale = Math.max(
                      canvas.width / tempCanvas.width,
                      canvas.height / tempCanvas.height
                    )
                    const x = (canvas.width - tempCanvas.width * scale) / 2
                    const y = (canvas.height - tempCanvas.height * scale) / 2

                    ctx.drawImage(
                      tempCanvas,
                      x,
                      y,
                      tempCanvas.width * scale,
                      tempCanvas.height * scale
                    )
                  }
                }
                previews[filter._id] = canvas
              }

              previewImageBitmap.close()
            } catch (error) {
              console.error('Error loading filter preview image for thumbnails:', error)
              // Fall through to live preview as last resort
            }
          }

          // If we still don't have previews (no cached thumbnails and no preview image URL),
          // fall back to live preview
          if (Object.keys(previews).length === 0) {
            const cameraBlob = await unifiedCameraUtils.capturePreview(previewCameraKey)
            const rawBlob = cameraBlob || null

            if (!rawBlob) {
              if (!cancelled) setStripThumbsFromLiveCameraBlob(false)
              return
            }

            thumbsFromLiveCameraBlob = true
            for (const filter of filters) {
              const canvas = document.createElement('canvas')
              canvas.width = 144
              canvas.height = 144
              const ctx = canvas.getContext('2d', {
                willReadFrequently: true
              }) as unknown as OffscreenCanvasRenderingContext2D
              if (ctx) {
                try {
                  const filterFn = (ctx: CanvasRenderingContext2D): void => {
                    if (filter.filterMode === 'lut') {
                      applyLUTThenOverlay(ctx, filter)
                    } else {
                      filterUtils.applyAdjustments(ctx, filter.adjustments || {})
                    }
                  }
                  const previewFrame = await imageUtils.processPreview(rawBlob, {
                    ratio: normalizedCaptureRatio,
                    filter: filterFn
                  })
                  if (previewFrame instanceof Blob) {
                    const imageBitmap = await createImageBitmap(previewFrame)
                    const scale = Math.max(
                      canvas.width / imageBitmap.width,
                      canvas.height / imageBitmap.height
                    )
                    const x = (canvas.width - imageBitmap.width * scale) / 2
                    const y = (canvas.height - imageBitmap.height * scale) / 2
                    ctx.drawImage(
                      imageBitmap,
                      x,
                      y,
                      imageBitmap.width * scale,
                      imageBitmap.height * scale
                    )
                    imageBitmap.close()
                    await bakeStripThumbnailOrientation(canvas, stripR, stripM)
                  }
                } catch (error) {
                  console.error('Error creating preview for filter:', filter.id, error)
                }
              }
              previews[filter._id] = canvas
            }
          }
        } else {
          // Use live camera preview (original behavior)
          const cameraBlob = await unifiedCameraUtils.capturePreview(previewCameraKey)
          const rawBlob = cameraBlob || null

          if (!rawBlob) {
            if (!cancelled) setStripThumbsFromLiveCameraBlob(false)
            return
          }

          thumbsFromLiveCameraBlob = true
          for (const filter of filters) {
            const canvas = document.createElement('canvas')
            canvas.width = 144
            canvas.height = 144
            const ctx = canvas.getContext('2d', {
              willReadFrequently: true
            }) as unknown as OffscreenCanvasRenderingContext2D
            if (ctx) {
              try {
                const filterFn = (ctx: CanvasRenderingContext2D): void => {
                  if (filter.filterMode === 'lut') {
                    applyLUTThenOverlay(ctx, filter)
                  } else {
                    filterUtils.applyAdjustments(ctx, filter.adjustments || {})
                  }
                }
                const previewFrame = await imageUtils.processPreview(rawBlob, {
                  ratio: normalizedCaptureRatio,
                  filter: filterFn
                })
                if (previewFrame instanceof Blob) {
                  const imageBitmap = await createImageBitmap(previewFrame)
                  const scale = Math.max(
                    canvas.width / imageBitmap.width,
                    canvas.height / imageBitmap.height
                  )
                  const x = (canvas.width - imageBitmap.width * scale) / 2
                  const y = (canvas.height - imageBitmap.height * scale) / 2
                  ctx.drawImage(
                    imageBitmap,
                    x,
                    y,
                    imageBitmap.width * scale,
                    imageBitmap.height * scale
                  )
                  imageBitmap.close()
                  await bakeStripThumbnailOrientation(canvas, stripR, stripM)
                }
              } catch (error) {
                console.error('Error creating preview for filter:', filter.id, error)
              }
            }
            previews[filter._id] = canvas
          }
        }
      }

      if (!cancelled) {
        setStripThumbsFromLiveCameraBlob(thumbsFromLiveCameraBlob)
        setPreviewCanvases(previews)
      }
    }

    void createFilterPreviews()

    return (): void => {
      cancelled = true
    }
  }, [
    applyAfterCapture,
    filterPageUseLivePreview,
    filterPreviewImageUrlConfig,
    filterPreviewGallerySourceKey,
    filters,
    previewCameraKey,
    normalizedCaptureRatio,
    primaryPreviewRotation,
    primaryMirror,
    secondaryPreviewRotation,
    secondaryMirror
  ])

  interface CustomArrowProps {
    onClick?: () => void
  }

  const NextArrow = ({ onClick }: CustomArrowProps): JSX.Element => {
    return (
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 cursor-pointer text-white hover:text-veranda transition-colors"
        onClick={onClick}
        role="button"
        aria-label={t('filter.nextFilter', 'Next filter')}
      >
        <FaChevronRight size={isTiny ? 18 : filterStripLandscapeTight ? 20 : 24} />
      </div>
    )
  }

  const PrevArrow = ({ onClick }: CustomArrowProps): JSX.Element => {
    return (
      <div
        aria-label={t('filter.prevFilter', 'Previous filter')}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 cursor-pointer text-white hover:text-veranda transition-colors"
        onClick={onClick}
        role="button"
      >
        <FaChevronLeft size={isTiny ? 18 : filterStripLandscapeTight ? 20 : 24} />
      </div>
    )
  }

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: '0px',
    focusOnSelect: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (_: number, next: number): void => {
      setSelectedFilter(filters[next % filters.length]._id)
    },
    initialSlide: 0
  }

  // Duplicate filters array to prevent empty slides
  const extendedFilters = [...filters, ...filters]

  const showFrameBasedSharedCountdown = isAfterCapture && captureMode === 'frame-based'
  const showPageCountdownTop = showFrameBasedSharedCountdown || usePageFilterCountdown

  return (
    <div className="relative h-full w-full min-h-0 overflow-hidden bg-black" data-page="filter">
      {/* Processing Modal */}
      <ProcessingModal
        isVisible={isProcessing}
        title={t('filter.processing', 'Processing Filter...')}
        message={t('filter.processingMessage', 'Applying filter to your photos. Please wait...')}
      />

      {/* Preview Container */}
      <div className="absolute inset-0 m-auto flex min-h-0 items-center justify-center">
        <div className="relative flex h-full w-full min-h-0 items-center justify-center">
          {applyAfterCapture && capturedImageUrl ? (
            <FilteredImagePreview
              key={capturedImageUrl}
              imageUrl={capturedImageUrl}
              filterAdjustments={
                filters.find((f) => f._id === selectedFilter)?.adjustments as
                  | Record<string, number>
                  | undefined
              }
              cachedFilteredUrl={
                gallery.capturedItems.find((item) => item.photoUrl === capturedImageUrl)
                  ?.filteredVersions?.[selectedFilter || '']
              }
              filterMode={filters.find((f) => f._id === selectedFilter)?.filterMode}
              filterId={selectedFilter || undefined}
              postLUTAdjustments={filters.find((f) => f._id === selectedFilter)?.postLUTAdjustments}
            />
          ) : (
            <Preview
              key={`${selectedFilter}-${previewCameraKey}-${normalizedCaptureRatio ?? 'auto'}`}
              adjustments={filters.find((f) => f._id === selectedFilter)?.adjustments}
              cameraKey={previewCameraKey}
              filter={selectedFilter || ''}
              postLUTAdjustments={filters.find((f) => f._id === selectedFilter)?.postLUTAdjustments}
            />
          )}

          {/* Filter List - Positioned relative to Preview */}
          <div
            className="absolute z-50"
            style={{
              width: isLandscape ? `${screenHeight}px` : `${screenWidth}px`,
              bottom: `${filterSliderBottomPx}px`
            }}
            role="listbox"
          >
            <Slider ref={sliderRef} {...sliderSettings}>
              {extendedFilters.map((filter, index) => (
                <div
                  aria-label={filter.name}
                  key={`${filter._id}-${index}`}
                  className={filterSlidePadding}
                  role="option"
                >
                  <div className={`flex flex-col items-center ${filterSliderRowClass}`}>
                    <div
                      className={`${filterThumbClass} rounded-full overflow-hidden cursor-pointer ${
                        selectedFilter === filter._id
                          ? `${isTiny || filterStripLandscapeTight ? 'ring-2' : 'ring-4'} ring-veranda bg-gray-700`
                          : 'ring-2 ring-white bg-gray-500 opacity-70'
                      }`}
                      style={{
                        transform:
                          selectedFilter === filter._id
                            ? `scale(${filterSelectedScale})`
                            : 'scale(1)',
                        transition: 'transform 0.2s ease-in-out, opacity 0.2s ease-in-out'
                      }}
                    >
                      {previewCanvases[filter._id] && (
                        <div
                          className="flex h-full w-full items-center justify-center"
                          style={
                            liveFilterStripTransform
                              ? {
                                  transform: liveFilterStripTransform,
                                  transformOrigin: 'center center'
                                }
                              : undefined
                          }
                        >
                          <FilterCarouselThumb source={previewCanvases[filter._id]} />
                        </div>
                      )}
                    </div>
                    <div
                      className={`flex items-center justify-center ${
                        isTiny
                          ? 'mt-1 h-8'
                          : filterUiCompact
                            ? 'mt-2 h-12'
                            : filterStripLandscapeTight
                              ? 'mt-2 h-10'
                              : 'mt-6 h-12'
                      }`}
                      style={{
                        transform:
                          selectedFilter === filter._id
                            ? `scale(${filterSelectedScale})`
                            : 'scale(1)',
                        transition: 'transform 0.2s ease-in-out'
                      }}
                    >
                      <span
                        className={`${filterNameTextClass} ${
                          selectedFilter === filter._id ? 'text-veranda' : 'text-white opacity-70'
                        } ${filterStripLandscapeTight ? 'max-w-[8rem] truncate text-center' : ''}`}
                      >
                        {filter.name}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>

      {/* Bottom-right anchor; timer centered over button (not narrower text hugging the right edge) */}
      <div
        className={`absolute z-[60] flex flex-col items-center gap-2 ${filterUiCompact ? 'bottom-10 right-3' : 'bottom-24 right-12'}`}
      >
        {showPageCountdownTop && (
          <span
            className={`block text-center font-mono tabular-nums leading-none text-white drop-shadow-md ${filterUiCompact ? 'text-lg' : 'text-2xl'}`}
          >
            {showFrameBasedSharedCountdown ? sharedRemainingTime : remainingTime}
          </span>
        )}
        <Button
          text={t('common.next', 'Next')}
          onClick={handleNext}
          data-nav-to={nextPage}
          disabled={isProcessing || !selectedFilter}
          className={filterUiCompact ? '!px-4 !py-2 !text-sm shadow-lg' : undefined}
        />
      </div>
    </div>
  )
}

export default FilterPage
